import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DerivativeSearchFacade } from '@zero/leverage-products/domain';
import { AutofocusDirective } from '@zero/shared/ui-directives';
import { ModalComponent } from '@zero/shared/ui-web-design-system';
import { DerivativeSearchValue, LeverageProductType } from '@zero/shared/util-models';
import { filter, take } from 'rxjs';

@Component({
  selector: 'leverage-products-derivative-search-navigation',
  templateUrl: './derivative-search-navigation.component.html',
  styleUrls: ['./derivative-search-navigation.component.scss'],
  imports: [NgTemplateOutlet, FormsModule, AsyncPipe, TranslocoPipe, ModalComponent, AutofocusDirective],
})
export class DerivativeSearchNavigationComponent implements OnChanges {
  @Input() public isCustomLeverageSelected = false;
  @Input() public preselectedInvestmentType: LeverageProductType = LeverageProductType.CALL;

  public readonly facade = inject(DerivativeSearchFacade);
  public readonly modal = inject(NgbModal);
  public readonly LeverageProductType = LeverageProductType;

  public customStrike = this.#initialCustomValue();
  public customDuration = this.#initialCustomValue();
  public customCap = this.#initialCustomValue();
  public customFactor = this.#initialCustomValue();
  public isCustomStrikeSelected = false;
  public isCustomDurationSelected = false;
  public isCustomCapSelected = false;
  public isCustomFactorSelected = false;
  public isQuickMode: boolean;

  constructor() {
    this.facade.quickMode$.pipe(take(1)).subscribe((isQuickMode) => (this.isQuickMode = isQuickMode));
  }

  ngOnChanges(changes: SimpleChanges): void {
    changes['preselectedInvestmentType']?.currentValue && this.facade.toggleLeverageType(this.preselectedInvestmentType);
  }

  #getCustomSearchValue(searchValue: DerivativeSearchValue): DerivativeSearchValue {
    const { lower, upper } = searchValue;
    return lower ? { lower, upper: upper ?? null } : { lower: 0, upper: upper ?? null };
  }

  #initialCustomValue(): DerivativeSearchValue {
    return { lower: null, upper: null };
  }

  public applyCustomStrike = (): void => {
    if (!this.customStrike) {
      return;
    }
    this.isCustomStrikeSelected = false;
    this.facade.useStrike(this.#getCustomSearchValue(this.customStrike));
  };

  public applyStrike = (strike?: DerivativeSearchValue): void => {
    this.facade.currentStrike$
      .pipe(
        take(1),
        filter((currentStrike) => currentStrike !== strike),
      )
      .subscribe(() => {
        this.customStrike = this.#initialCustomValue();
        this.facade.useStrike(strike);
      });
  };

  public applyCustomDuration = (): void => {
    if (!this.customDuration) {
      return;
    }
    const { lower, upper } = this.customDuration;
    if (!!lower && !!upper && lower > upper) {
      return;
    }

    if (!lower && !upper) {
      return;
    }

    this.isCustomDurationSelected = false;
    const daysToMonth = (days: number): number => days * 30;
    this.facade.useDuration(
      lower
        ? { lower: daysToMonth(lower), upper: upper ? daysToMonth(upper) : null }
        : { lower: 0, upper: upper ? daysToMonth(upper) : null },
    );
  };

  public applyDuration = (duration?: DerivativeSearchValue): void => {
    this.facade.currentDuration$
      .pipe(
        take(1),
        filter((currentDuration) => currentDuration !== duration),
      )
      .subscribe(() => {
        this.customDuration = this.#initialCustomValue();
        this.facade.useDuration(duration);
      });
  };

  public applyCustomCap = (): void => {
    if (!this.customCap) {
      return;
    }
    this.isCustomCapSelected = false;
    this.facade.useCap(this.#getCustomSearchValue(this.customCap));
  };

  public applyCap = (cap?: DerivativeSearchValue): void => {
    this.facade.currentCap$
      .pipe(
        take(1),
        filter((currentCap) => currentCap !== cap),
      )
      .subscribe(() => {
        this.customCap = this.#initialCustomValue();
        this.facade.useCap(cap);
      });
  };

  public applyCustomFactor = (): void => {
    if (!this.customFactor) {
      return;
    }
    this.isCustomFactorSelected = false;
    this.customFactor.upper = this.customFactor.lower;
    this.facade.useFactor({ ...this.customFactor });
  };

  public applyFactor = (factor?: DerivativeSearchValue): void => {
    this.facade.currentFactor$
      .pipe(
        take(1),
        filter((currentFactor) => currentFactor !== factor),
      )
      .subscribe(() => {
        this.customFactor = this.#initialCustomValue();
        this.facade.useFactor(factor);
      });
  };

  public applyLeverage = (leverage: number): void => {
    this.facade.currentLeverage$
      .pipe(
        take(1),
        filter((currentLeverage) => currentLeverage !== leverage),
      )
      .subscribe(() => {
        this.isCustomLeverageSelected = false;
        this.facade.useLeverage(leverage);
      });
  };

  public useCustomLeverage = (): void => {
    if (!this.facade.isValidCustomLeverage()) {
      return;
    }
    this.isCustomLeverageSelected = false;
    this.facade.useCustomLeverage();
  };
}
